import React, { useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Cookies from "js-cookie";

// Public Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./components/Home";
import AboutUs from "./components/AboutUs";
import Infrastructure from "./components/Infrastructure";
import Contact from "./components/Contact";
import EcoCrafted from "./components/EcoCrafted";
import ProductDetails from "./components/ProductDetails";
import Luxicore from "./components/Luxicore";
import VivaWood from "./components/VivaWood";
import Warranty from "./components/Warranty";
import InstallationGuide from "./components/InstallationGuide";
import ProductSpecs from "./components/ProductSpecs";

// Admin Components
import Admin from "./components/Admin/Admin";
import ForgotPassword from "./components/Admin/ForgotPassword";
import Dashboard from "./components/dashboard/Dashboard";
import AddCategoryPage from "./components/dashboard/sections/AddCategoryPage";

// Admin Layout Components
import Sidebar from "./components/dashboard/Sidebar";
import CategoryManagementSection from "./components/dashboard/sections/CategoryManagement";
import ProductManagementSection from "./components/dashboard/sections/ProductManagementSection";
import AdminHeader from "./components/dashboard/Header";
import EditCategoryPage from "./components/dashboard/sections/EditCategoryPage";
import AddProductPage from "./components/dashboard/sections/AddProductPage";
import EditProductPage from "./components/dashboard/sections/EditProductPage";
import UserManagementSection from "./components/dashboard/sections/UserManagementSection";
import AddUserPage from "./components/dashboard/sections/AddUserPage";
import EditUserPage from "./components/dashboard/sections/EditUserPage";
import ProtectedRoute from "./components/ProtactedRoute";
import { ToastContainer } from "react-toastify";
import AuthPage from "./components/AuthPage";
import ProfilePage from "./components/profile";
import ThankYouPage from "./components/ThankYou";
import Orders from "./components/dashboard/sections/Orders";
import OrdersByUser from "./components/user-order";
import SignUp from "./components/SignUp";
import ForgotPasswordPage from "./components/ForgetPassword";
import OtpVerified from "./components/OtpVerified";
import ProductCards from "./components/ProductList";
import CartPage from "./components/Cart";
import Category from "./components/Category";
import CategoryProductCards from "./components/CategoryProductList";
import { CartProvider } from "./components/cartContext";

// Public Layout Component
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Footer />
    </>
  );
};

// Admin Layout Component
const AdminLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user] = useState({ name: "Admin User", email: "admin@example.com" });
  const [notifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNotificationRead = (id) => {
    // Handle notification read logic
    console.log("Notification read:", id);
  };

  const handleLogout = () => {
    // Handle logout logic
    window.location.href = '/admin';
  };

  return (
    <div className="admin-layout">

      <div className={`dashboard-main ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>

        <main className="admin-content d-flex">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
          />
          <div className={`admin-content-wrapper ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
            <AdminHeader
              user={user}
              notifications={notifications}
              onNotificationRead={handleNotificationRead}
              onLogout={handleLogout}
              onToggleSidebar={toggleSidebar}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            {children}
          </div>
        </main>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

// Main App Component
const App = () => {
  const [cartCount, setCartCount] = useState(0);


  const location = useLocation();

  // Define admin routes
  const adminRoutes = [
    '/admin',
    '/forgotpassword',
    '/dashboard'
  ];

  // Check if current route starts with admin paths
  const isAdminRoute = adminRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith('/dashboard')
  );

  return (
    <CartProvider>
      <Routes>
        {/* Public Routes with Public Layout */}
        <Route
          path="/"
          element={
            <PublicLayout>
              <Home />
            </PublicLayout>
          }
        />
        <Route
          path="/about-us"
          element={
            <PublicLayout>
              <AboutUs />
            </PublicLayout>
          }
        />
        <Route
          path="/infrastructure"
          element={
            <PublicLayout>
              <Infrastructure />
            </PublicLayout>
          }
        />
        <Route
          path="/contact"
          element={
            <PublicLayout>
              <Contact />
            </PublicLayout>
          }
        />
        <Route
          path="/log-in"
          element={
            <PublicLayout>
              <AuthPage />
            </PublicLayout>
          }
        />
        <Route
          path="/sign-up"
          element={
            <PublicLayout>
              <SignUp />
            </PublicLayout>
          }
        />
        <Route
          path="/forget-password"
          element={
            <PublicLayout>
              <ForgotPasswordPage />
            </PublicLayout>
          }
        />
        <Route
          path="/otp-varified"
          element={
            <PublicLayout>
              <OtpVerified />
            </PublicLayout>
          }
        />

        <Route
          path="/products"
          element={
            <PublicLayout>
              <ProductCards />
            </PublicLayout>
          }
        />

        <Route
          path="/new-category"
          element={
            <PublicLayout>
              <Category />
            </PublicLayout>
          }
        />
        <Route
          path="/product-category/:id"
          element={
            <PublicLayout>
              <CategoryProductCards />
            </PublicLayout>
          }
        />
        <Route
          path="/cart-page"
          element={
            <PublicLayout>
              <CartPage />
            </PublicLayout>
          }
        />
        <Route
          path="/new-category/:id"
          element={
            <PublicLayout>
              <EcoCrafted />
            </PublicLayout>
          }
        />
        <Route
          path="/category/:id"
          element={
            <PublicLayout>
              <EcoCrafted />
            </PublicLayout>
          }
        />
        <Route
          path="/product-details/:productId"
          element={
            <PublicLayout>
              <ProductDetails />
            </PublicLayout>
          }
        />
        <Route
          path="/luxicore-legacy"
          element={
            <PublicLayout>
              <Luxicore />
            </PublicLayout>
          }
        />
        <Route
          path="/viva-wood"
          element={
            <PublicLayout>
              <VivaWood />
            </PublicLayout>
          }
        />
        <Route
          path="/warranty"
          element={
            <PublicLayout>
              <Warranty />
            </PublicLayout>
          }
        />

        <Route
          path="/profile"
          element={
            <PublicLayout>
              <ProfilePage />
            </PublicLayout>
          }
        />
        <Route
          path="/installation-guide"
          element={
            <PublicLayout>
              <InstallationGuide />
            </PublicLayout>
          }
        />
        <Route
          path="/product-specs"
          element={
            <PublicLayout>
              <ProductSpecs />
            </PublicLayout>
          }
        />
        <Route
          path="/order-complate"
          element={
            <PublicLayout>
              <ThankYouPage />
            </PublicLayout>
          }
        />
        <Route
          path="/orders"
          element={
            <PublicLayout>
              <OrdersByUser />
            </PublicLayout>

          }
        />

        {/* Admin Routes without Layout (Login/Auth pages) */}
        <Route path="/admin" element={<Admin />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />

        {/* Dashboard Routes with Admin Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Dashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/categories"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <CategoryManagementSection />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/add-category"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AddCategoryPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/edit-category/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditCategoryPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/products"
          element={
            <protectedRoute>
              <AdminLayout>
                <ProductManagementSection />
              </AdminLayout>
            </protectedRoute>
          }
        />
        <Route
          path="/dashboard/add-product"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AddProductPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/edit-product/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditProductPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/user"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UserManagementSection />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/add-user"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AddUserPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/orders"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Orders />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/edit-user/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <EditUserPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

      </Routes>
    </CartProvider>

  );
};

export default App;